// The Akaiva sign-in frame for the signed-out and account screens, shared with the admin console
// and the driver app (constitution 1.1.0; FR-027 exception): the backdrop; the Akaiva logo and a
// welcome line on the left; one card (max width 480) holding the screen on the right. Below the
// desktop breakpoint the parts stack, full width with 16 px margins. Scrolls when the keyboard or a
// small screen needs it. Controls inside take the admin console's sign-in look
// (src/ui/signInFrame.ts). Phones: the backdrop runs under the status bar, the card is a sheet
// anchored to the bottom edge (full width, top corners rounded) so the actions sit under the thumb,
// and the sheet rides up with the keyboard; an up arrow leads back when there is somewhere to go.
import { Image } from "expo-image";
import { useRouter } from "expo-router";
import type { ReactNode } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { makeStyles } from "@/theme/makeStyles";
import { useTheme } from "@/theme/useTheme";
import { ChevronIcon } from "@/ui/icons/ChevronIcon";
import { PressableTarget } from "@/ui/PressableTarget";
import { SignInFrameContext } from "@/ui/signInFrame";
import { Text } from "@/ui/Text";

import LOGO from "../../../assets/akaiva-loader-static.png";
import { useClaimStatusBar } from "../systemBars";
import { AuthBackdrop } from "./AuthBackdrop";

/** The card's maximum width (the admin console's sign-in card). */
export const AUTH_FRAME_MAX_WIDTH = 480;

/** Logo artwork is 400 × 303. */
const LOGO_ASPECT = 400 / 303;

/** The welcome line beside the card. */
export type AuthIntro = { title: string; text: string };

const DEFAULT_INTRO: AuthIntro = {
  title: "Akaiva Edu Power",
  text: "School books and stationery, from your school's own booklist",
};

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    screen: { flex: 1, backgroundColor: t.color.scene },
    scroll: {
      flexGrow: 1,
      paddingHorizontal: t.space.lg,
      paddingTop: t.space.lg,
      paddingBottom: t.space.x3l,
      gap: t.space.x2l,
    },
    scrollWide: {
      flexDirection: "row",
      paddingHorizontal: t.space.x3l + t.space.lg,
      paddingVertical: t.space.x2l,
      gap: t.space.x3l + t.space.lg,
    },
    side: { gap: t.space.lg, alignItems: "flex-start" },
    sideWide: { flex: 1, gap: t.space.x3l },
    intro: { gap: t.space.sm },
    introWide: { paddingLeft: t.space.x3l + t.space.lg },
    center: { alignItems: "center" },
    centerWide: { flex: 1, justifyContent: "center" },
    card: {
      width: "100%",
      maxWidth: AUTH_FRAME_MAX_WIDTH,
      gap: t.space.x2l,
      borderWidth: t.borderWidth.card,
      borderColor: t.color.sceneEdge,
      borderRadius: t.radius.card,
      backgroundColor: t.color.sceneCard,
    },
    // Phones.
    fill: { flex: 1 },
    phoneScroll: { paddingHorizontal: 0, paddingBottom: 0 },
    phoneSide: { paddingHorizontal: t.space.lg },
    phoneTop: { flexDirection: "row", alignItems: "center", gap: t.space.xs },
    up: { marginLeft: -t.space.sm, borderRadius: t.radius.button },
    // The sheet takes the rest of the screen and holds its content at the bottom edge.
    phoneCenter: { flexGrow: 1, justifyContent: "flex-end" },
    sheet: {
      maxWidth: undefined,
      borderWidth: 0,
      borderTopWidth: t.borderWidth.card,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
  }),
);

/**
 * For a signed-out screen opened from another (Log in, Sign up, Forgot password): the up arrow's
 * action, or undefined when there is nowhere to go back to (opened from a link, or the first screen).
 */
export function useAuthBack(): (() => void) | undefined {
  const router = useRouter();
  return router.canGoBack() ? () => router.back() : undefined;
}

export type AuthFrameProps = {
  children: ReactNode;
  /** Phones: an up arrow beside the logo (useAuthBack). */
  onBack?: () => void;
  /** The welcome line beside the card; a general welcome when left out. */
  intro?: AuthIntro;
  testID?: string;
};

export function AuthFrame({
  children,
  onBack,
  intro = DEFAULT_INTRO,
  testID,
}: AuthFrameProps) {
  const styles = useStyles();
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const wide = width >= theme.breakpoint.desktop;
  const phone = !wide && Platform.OS !== "web";
  const logoWidth = wide ? 168 : 120;
  const showBack = phone && onBack !== undefined;
  // Phones: the status bar takes the scene colour, so the frame reads as one surface to the top.
  useClaimStatusBar(phone ? theme.color.scene : null);
  return (
    <SignInFrameContext.Provider value={true}>
      <View style={styles.screen} testID={testID}>
        <AuthBackdrop />
        <KeyboardAvoidingView
          style={styles.fill}
          behavior={phone ? "padding" : undefined}
        >
          <ScrollView
            contentContainerStyle={[
              styles.scroll,
              wide && styles.scrollWide,
              phone && styles.phoneScroll,
              phone && { paddingTop: theme.space.sm },
            ]}
            keyboardShouldPersistTaps="handled"
            role="main"
            testID={testID ? `${testID}-scroll` : undefined}
          >
            <View
              style={[
                styles.side,
                wide && styles.sideWide,
                phone && styles.phoneSide,
              ]}
            >
              <View role="banner" style={phone ? styles.phoneTop : undefined}>
                {showBack ? (
                  <PressableTarget
                    accessibilityLabel="Back"
                    onPress={onBack}
                    style={styles.up}
                    android_ripple={{
                      color: theme.color.border,
                      borderless: true,
                    }}
                    testID="auth-back"
                  >
                    <ChevronIcon
                      direction="left"
                      color={theme.color.text}
                      size={theme.size.icon.lg}
                    />
                  </PressableTarget>
                ) : null}
                <Image
                  source={LOGO}
                  style={{ width: logoWidth, height: logoWidth / LOGO_ASPECT }}
                  contentFit="contain"
                  accessible
                  accessibilityLabel="Akaiva"
                  role="img"
                />
              </View>
              <View style={[styles.intro, wide && styles.introWide]}>
                <Text
                  variant="heading"
                  size={wide ? "x6l" : phone ? "x5l" : "x4l"}
                >
                  {intro.title}
                </Text>
                <Text tone="muted" size={wide ? "xl" : "lg"}>
                  {intro.text}
                </Text>
              </View>
            </View>
            <View
              style={[
                styles.center,
                wide && styles.centerWide,
                phone && styles.phoneCenter,
              ]}
            >
              <View
                style={[
                  styles.card,
                  {
                    padding: wide
                      ? theme.space.x3l + theme.space.lg
                      : theme.space.x2l,
                  },
                  phone && styles.sheet,
                  phone && {
                    paddingHorizontal: theme.space.lg,
                    paddingBottom: theme.space.x2l + insets.bottom,
                  },
                ]}
                testID={testID ? `${testID}-card` : undefined}
              >
                {children}
              </View>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </View>
    </SignInFrameContext.Provider>
  );
}
